import { Link } from 'react-router-dom';
import Footer from '../../home/components/Footer';

export default function EstatePlanningArticle() {
  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm shadow-sm">
        <div className="max-w-7xl mx-auto px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <img
              src="https://static.readdy.ai/image/8dbd60c8f218d42716666e4ddaa548a8/e10d3fd4f000823b1408934f8a9fe8ab.png"
              alt="AMM Management Group"
              className="h-12"
            />
          </Link>
          <Link
            to="/#resources"
            className="text-sm text-[#0a1628] hover:text-[#00e5c7] transition-colors font-semibold"
          >
            ← Back to Resources
          </Link>
        </div>
      </nav>

      {/* Article Content */}
      <article className="pt-32 pb-24 px-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-12">
            <span className="inline-block px-3 py-1 bg-[#00e5c7] text-[#0a1628] text-xs uppercase tracking-wider rounded-md font-semibold mb-4">
              ESTATE PLANNING
            </span>
            <h1
              className="text-4xl md:text-5xl font-bold text-[#0a1628] mb-6 leading-tight"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              Estate Tax Planning: Protecting Your Legacy
            </h1>
            <div className="flex items-center text-[#5a6a7e] text-sm">
              <span>February 28, 2024</span>
              <span className="mx-3">•</span>
              <span>11 min read</span>
            </div>
          </div>

          {/* Featured Image */}
          <div className="w-full aspect-[16/9] rounded-lg overflow-hidden mb-12">
            <img
              src="https://public.readdy.ai/ai/img_res/913fef210a0cd10b5a235d6c899638d7.jpg"
              alt="Estate Planning"
              className="w-full h-full object-cover object-top"
            />
          </div>

          {/* Article Body */}
          <div className="prose prose-lg max-w-none">
            <p className="text-lg text-[#5a6a7e] leading-relaxed mb-6">
              Estate tax planning is essential for preserving your wealth and ensuring your assets are distributed according to your wishes. With proper planning, you can minimize estate taxes, avoid probate, and protect your legacy for future generations. This comprehensive guide covers the key strategies and considerations for effective estate tax planning.
            </p>

            <h2 className="text-2xl font-bold text-[#0a1628] mt-12 mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Understanding Estate Taxes
            </h2>
            <p className="text-[#5a6a7e] leading-relaxed mb-6">
              The federal estate tax applies to the transfer of property at death. For 2024, the federal estate tax exemption is $13.61 million per individual ($27.22 million for married couples). Estates valued above these thresholds are subject to federal estate tax rates up to 40%.
            </p>
            <p className="text-[#5a6a7e] leading-relaxed mb-6">
              However, this high exemption is scheduled to sunset in 2026, potentially reverting to approximately $7 million per person (adjusted for inflation). This makes current planning even more critical for high-net-worth individuals.
            </p>

            <h2 className="text-2xl font-bold text-[#0a1628] mt-12 mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Essential Estate Planning Documents
            </h2>

            <h3 className="text-xl font-bold text-[#0a1628] mt-8 mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Last Will and Testament
            </h3>
            <p className="text-[#5a6a7e] leading-relaxed mb-4">
              A will specifies how your assets should be distributed after death and names guardians for minor children. Without a will, state intestacy laws determine asset distribution, which may not align with your wishes. Keep your will updated as your circumstances change.
            </p>

            <h3 className="text-xl font-bold text-[#0a1628] mt-8 mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Revocable Living Trust
            </h3>
            <p className="text-[#5a6a7e] leading-relaxed mb-4">
              A living trust allows assets to pass to beneficiaries without going through probate, saving time and money. You maintain control of assets during your lifetime and can modify the trust as needed. Assets in a living trust are still included in your taxable estate but avoid the public probate process.
            </p>

            <h3 className="text-xl font-bold text-[#0a1628] mt-8 mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Power of Attorney
            </h3>
            <p className="text-[#5a6a7e] leading-relaxed mb-4">
              A durable power of attorney designates someone to manage your financial affairs if you become incapacitated. A healthcare power of attorney (or healthcare proxy) designates someone to make medical decisions on your behalf. These documents are crucial for ensuring your wishes are carried out if you're unable to make decisions yourself.
            </p>

            <h2 className="text-2xl font-bold text-[#0a1628] mt-12 mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Tax-Saving Strategies
            </h2>

            <h3 className="text-xl font-bold text-[#0a1628] mt-8 mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Annual Gift Tax Exclusion
            </h3>
            <p className="text-[#5a6a7e] leading-relaxed mb-4">
              For 2024, you can gift up to $18,000 per person per year without using any of your lifetime estate tax exemption. Married couples can combine their exclusions to gift $36,000 per recipient annually. This strategy allows you to transfer wealth to heirs while reducing your taxable estate.
            </p>

            <h3 className="text-xl font-bold text-[#0a1628] mt-8 mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Irrevocable Life Insurance Trust (ILIT)
            </h3>
            <p className="text-[#5a6a7e] leading-relaxed mb-4">
              Life insurance proceeds are generally included in your taxable estate. By placing life insurance policies in an ILIT, the death benefit passes to beneficiaries estate-tax-free. The trust owns the policy, removing it from your estate while providing liquidity to pay estate taxes or other expenses.
            </p>

            <h3 className="text-xl font-bold text-[#0a1628] mt-8 mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Grantor Retained Annuity Trust (GRAT)
            </h3>
            <p className="text-[#5a6a7e] leading-relaxed mb-4">
              A GRAT allows you to transfer appreciating assets to beneficiaries with minimal gift tax consequences. You receive annuity payments for a specified term, and any appreciation above the IRS assumed rate passes to beneficiaries tax-free. This is particularly effective for assets expected to appreciate significantly.
            </p>

            <h3 className="text-xl font-bold text-[#0a1628] mt-8 mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Qualified Personal Residence Trust (QPRT)
            </h3>
            <p className="text-[#5a6a7e] leading-relaxed mb-4">
              A QPRT allows you to transfer your home to beneficiaries at a reduced gift tax value while continuing to live in it for a specified term. After the term ends, the home passes to beneficiaries, removing it from your estate. You can continue living in the home by paying fair market rent, which further reduces your estate.
            </p>

            <h3 className="text-xl font-bold text-[#0a1628] mt-8 mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Charitable Giving Strategies
            </h3>
            <p className="text-[#5a6a7e] leading-relaxed mb-4">
              Charitable donations reduce your taxable estate while supporting causes you care about. Consider these options:
            </p>
            <ul className="list-disc pl-6 text-[#5a6a7e] leading-relaxed mb-6 space-y-2">
              <li><strong className="text-[#0a1628]">Charitable Remainder Trust:</strong> Provides income during your lifetime with the remainder going to charity, offering immediate tax deductions and estate tax savings</li>
              <li><strong className="text-[#0a1628]">Charitable Lead Trust:</strong> Provides income to charity for a term, with assets eventually passing to beneficiaries at reduced gift tax values</li>
              <li><strong className="text-[#0a1628]">Donor-Advised Fund:</strong> Allows immediate tax deductions while distributing funds to charities over time</li>
            </ul>

            <h2 className="text-2xl font-bold text-[#0a1628] mt-12 mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Business Succession Planning
            </h2>
            <p className="text-[#5a6a7e] leading-relaxed mb-6">
              If you own a business, succession planning is critical:
            </p>
            <ul className="list-disc pl-6 text-[#5a6a7e] leading-relaxed mb-6 space-y-2">
              <li>Determine who will take over the business and when</li>
              <li>Consider using a family limited partnership or LLC to transfer business interests at discounted values</li>
              <li>Implement buy-sell agreements funded with life insurance</li>
              <li>Use installment sales or self-canceling installment notes (SCINs) to transfer business interests</li>
              <li>Consider an Employee Stock Ownership Plan (ESOP) for tax-advantaged business succession</li>
            </ul>

            <h2 className="text-2xl font-bold text-[#0a1628] mt-12 mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Beneficiary Designations
            </h2>
            <p className="text-[#5a6a7e] leading-relaxed mb-6">
              Many assets pass outside of your will through beneficiary designations, including:
            </p>
            <ul className="list-disc pl-6 text-[#5a6a7e] leading-relaxed mb-6 space-y-2">
              <li>Retirement accounts (401(k), IRA)</li>
              <li>Life insurance policies</li>
              <li>Bank and investment accounts with transfer-on-death (TOD) or payable-on-death (POD) designations</li>
            </ul>
            <p className="text-[#5a6a7e] leading-relaxed mb-6">
              Review and update beneficiary designations regularly, especially after major life events like marriage, divorce, births, or deaths. Outdated beneficiary designations can undermine your entire estate plan.
            </p>

            <h2 className="text-2xl font-bold text-[#0a1628] mt-12 mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
              State Estate Taxes
            </h2>
            <p className="text-[#5a6a7e] leading-relaxed mb-6">
              While federal estate tax exemptions are high, several states impose their own estate or inheritance taxes with much lower thresholds. If you live in or own property in states like New York, Massachusetts, Oregon, or Washington, state estate taxes may apply even if you're below the federal exemption. Consider the impact of state taxes in your planning.
            </p>

            <h2 className="text-2xl font-bold text-[#0a1628] mt-12 mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Regular Review and Updates
            </h2>
            <p className="text-[#5a6a7e] leading-relaxed mb-6">
              Estate planning is not a one-time event. Review your plan regularly, especially when:
            </p>
            <ul className="list-disc pl-6 text-[#5a6a7e] leading-relaxed mb-6 space-y-2">
              <li>Tax laws change</li>
              <li>Your family situation changes (marriage, divorce, births, deaths)</li>
              <li>Your financial situation changes significantly</li>
              <li>You move to a different state</li>
              <li>Your goals or wishes change</li>
            </ul>

            <h2 className="text-2xl font-bold text-[#0a1628] mt-12 mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Conclusion
            </h2>
            <p className="text-[#5a6a7e] leading-relaxed mb-6">
              Effective estate tax planning requires careful consideration of your unique circumstances, goals, and the complex interplay of federal and state tax laws. By implementing appropriate strategies and working with experienced professionals, you can minimize estate taxes, protect your assets, and ensure your legacy is preserved for future generations. Don't wait to start planning—the sooner you begin, the more options you'll have to protect your wealth.
            </p>

            <div className="mt-12 p-8 bg-[#0a1628] rounded-lg">
              <h3 className="text-xl font-bold text-white mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Protect Your Legacy with Expert Estate Planning
              </h3>
              <p className="text-white/80 mb-6">
                Our experienced team can help you develop a comprehensive estate plan that minimizes taxes and ensures your wishes are carried out.
              </p>
              <Link
                to="/#contact"
                className="inline-block px-6 py-3 bg-[#00e5c7] text-[#0a1628] rounded-lg font-semibold hover:bg-[#00e5c7]/90 transition-colors whitespace-nowrap"
              >
                Schedule a Consultation
              </Link>
            </div>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
}
